import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import * as DataTypeActions from './data-type.actions';
import { TableNames } from '../../services/utils/dexie.utils';
import { DexieService } from '../../services/dexie.service';
import { DataTypeService } from '../../services/data-type.service';
import { DataType } from '../../services/model';
import { selectDataTypes } from './data-type.selectors';

@Injectable({
  providedIn: 'root',
})
export class DataTypeEffects {
  loadDataTypes$;
  createDataType$;
  updateDataType$;
  deleteDataType$;
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly dataTypeService: DataTypeService,
    private readonly dexieService: DexieService
  ) {
    this.loadDataTypes$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DataTypeActions.fetchDataTypes),
        switchMap(() =>
          this.dexieService.allItems<DataType>(TableNames.DATA_TYPE).pipe(
            catchError(() => of([]))
          )
        ),
        tap((dataTypes) => {
          if (dataTypes.length > 0) {
            this.store.dispatch(DataTypeActions.loadDataTypes({ dataTypes }));
          }
        }),
        switchMap(() => this.dataTypeService.getAll()),
        switchMap((dataTypes) =>
          this.dexieService.updateTable(TableNames.DATA_TYPE, dataTypes).pipe(
            map(() => dataTypes),
            catchError(() => of(dataTypes))
          )
        ),
        map((dataTypes) => DataTypeActions.loadDataTypes({ dataTypes })),
        catchError((error) => {
          console.error('Error loading data types:', error);
          return of(DataTypeActions.loadDataTypes({ dataTypes: [] }));
        })
      )
    );

    this.createDataType$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DataTypeActions.createDataType),
        switchMap(({ dataType }) => this.dataTypeService.create(dataType)),
        switchMap((dataType) => this.dexieService.add(TableNames.DATA_TYPE, dataType).pipe(map(() => dataType))),
        withLatestFrom(this.store.select(selectDataTypes)),
        map(([newDataType, dataTypes]) => {
          let currentDataTypes = dataTypes ?? [];
          currentDataTypes = [...currentDataTypes, newDataType];
          this.dexieService.addOrUpdateItems(TableNames.DATA_TYPE, currentDataTypes);
          return DataTypeActions.loadDataTypes({ dataTypes: [...currentDataTypes] });
        })
      )
    );

    this.updateDataType$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DataTypeActions.updateDataType),
        switchMap(({ dataType }) => this.dataTypeService.update(dataType)),
        withLatestFrom(this.store.select(selectDataTypes)),
        map(([updatedDataType, dataTypes]) => {
          const currentDataTypes = dataTypes ?? [];
          const index = currentDataTypes.findIndex((dataType) => dataType.id === updatedDataType.id);
          if (index !== -1) {
            currentDataTypes[index] = updatedDataType;
          }
          this.dexieService.addOrUpdateItems(TableNames.DATA_TYPE, currentDataTypes);
          return DataTypeActions.loadDataTypes({ dataTypes: [...currentDataTypes] });
        })
      )
    );

    this.deleteDataType$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DataTypeActions.deleteDataType),
        concatMap(({ id }) =>
          this.dataTypeService.delete(id).pipe(
            withLatestFrom(this.store.select(selectDataTypes)),
            map(([, types]) => {
              const dataTypes = (types ?? []).filter((d) => d.id !== id);
              this.dexieService.addOrUpdateItems(TableNames.DATA_TYPE, dataTypes);
              return DataTypeActions.loadDataTypes({ dataTypes });
            })
          )
        )
      )
    );
  }
}
